import React, { createContext, useContext, useState, ReactNode } from "react";

// Define the types for Toast
interface Toast {
  id: string;
  message: string;
  type?: "success" | "error" | "info" | "warning";
}

interface ToastContextType {
  addToast: (message: string, type?: Toast["type"]) => void;
}

// Create the context
const ToastContext = createContext<ToastContextType | null>(null);

// Singleton to trigger toasts globally
let globalAddToast: ToastContextType["addToast"];

export const ToastProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const addToast = (message: string, type: Toast["type"] = "info") => {
    const id = Math.random().toString(36).substring(2, 9);
    setToasts((prevToasts) => [...prevToasts, { id, message, type }]);
    setTimeout(() => removeToast(id), 5000); // Auto-remove after 3 seconds
  };

  const removeToast = (id: string) => {
    setToasts((prevToasts) => prevToasts.filter((toast) => toast.id !== id));
  };

  globalAddToast = addToast; // Assign the singleton function

  return (
    <ToastContext.Provider value={{ addToast }}>
      {children}
      <div className="fixed top-4 right-4 space-y-1 z-50">
        {toasts.map((toast) => (
          <div
            key={toast.id}
            className={`relative p-3 rounded shadow text-white flex items-center justify-between ${
              toast.type === "success"
                ? "bg-green-500"
                : toast.type === "error"
                ? "bg-red-500"
                : toast.type === "warning"
                ? "bg-yellow-500"
                : "bg-blue-500"
            }`}
          >
            <span>{toast.message}</span>
            {/* Cross icon for dismiss */}
            <button
              className="ml-4 text-lg font-bold text-white focus:outline-none"
              onClick={() => removeToast(toast.id)}
            >
              &times;
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
};

// Hook to use toast inside React components
export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) throw new Error("useToast must be used within a ToastProvider");
  return context.addToast;
};

// Global toast function
export const showToast = (message: string, type?: Toast["type"]) => {
  if (globalAddToast) {
    globalAddToast(message, type);
  } else {
    console.error("ToastProvider is not mounted.");
  }
};
